import React, { useEffect, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import DataTable from "../../../../../Common/DataTable/DataTable";
import DataTableHeader from "../../../../../Common/DataTableHeader/DataTableHeader";
import DataTableFooter from "../../../../../Common/DataTableFooter/DataTableFooter";
import { dailyRoomOccupancyColumns } from "./DailyRoomOccupancyData";
import { setCurrentPage } from "../../../../../Redux/slice/features/paginationSilce";
import { REACT_APP_API_URL } from "../../../../../env";
import { fetchUserDetails, rehydrateAuthState } from "../../../../../Redux/slice/features/authSlice";

const DailyRoomOccupancyTable = () => {
  const today = new Date().toISOString().split("T")[0];
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentPage, itemsPerPage } = useSelector((state) => state.pagination);
  const [data, setData] = useState([]);
  const [hotelOptions, setHotelOptions] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: today,
    toDate: today,
    hotelId: 0,
  });
  const [dateError, setDateError] = useState(""); 

  const userHotelId = user?.user?.PropertyID;

  useEffect(() => {
    dispatch(rehydrateAuthState());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch]);

  const fetchHotels = useCallback(async () => {
    try {
      const url = userHotelId
        ? `${REACT_APP_API_URL}api/dashboard/admin/hotel/${userHotelId}`
        : `${REACT_APP_API_URL}api/dashboard/admin/hotels`;

      const response = await axios.get(url);
      const hotels = Array.isArray(response.data.data)
        ? response.data.data
        : [response.data.data];

      const formatted = hotels.map((h) => ({
        value: h.PropertyID,
        label: h.UnitName || h.PropertyName,
      }));

      setHotelOptions(formatted);

      if (userHotelId && formatted.length > 0) {
        setFilters((f) => ({ ...f, hotelId: userHotelId }));
      }
    } catch (err) {
      console.error("Error fetching hotels:", err);
    }
  }, [userHotelId]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const fetchReport = useCallback(async () => {
    const HotelID = userHotelId ?? filters.hotelId;
    if (!HotelID) {
      setDateError("Please select a Hotel.");
      return;
    }
    try {
      const res = await axios.get(`${REACT_APP_API_URL}api/dashboard/admin/occupancy`, {
        params: {
          HotelId: HotelID,
          FromDate: filters.fromDate,
          ToDate: filters.toDate
        }
      });

      const resData = res.data.data || [];
      const formatted = resData.map((item, index) => ({
        Sl: index + 1 + (currentPage - 1) * itemsPerPage,
        OccupancyDate: item.OccupancyDate ? item.OccupancyDate.split('T')[0] : "-",
        TotalCount: item.TotalCount || 0,
        RoomBreakup: item.RoomBreakup || "-"
      }));

      setData(formatted);
    } catch (err) {
      console.error("Error fetching report:", err);
      setData([]);
    }
  }, [userHotelId, filters, currentPage, itemsPerPage]);

  const handleShow = useCallback(() => {
    if (!filters.fromDate || !filters.toDate) {
      setDateError("Please select From Date and To Date.");
      return;
    }
    if (new Date(filters.fromDate) > new Date(filters.toDate)) {
      setDateError("From Date cannot be after To Date.");
      return;
    }
    setDateError("");
    dispatch(setCurrentPage(1));
    fetchReport();
  }, [filters, dispatch, fetchReport]);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return data.slice(start, start + itemsPerPage);
  }, [data, currentPage, itemsPerPage]);

  const handleNextPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage + 1));
  }, [dispatch, currentPage]);

  const handlePreviousPage = useCallback(() => {
    dispatch(setCurrentPage(currentPage - 1));
  }, [dispatch, currentPage]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <label htmlFor="checkin">Check-In</label>
        <input
          type="date"
          value={filters.fromDate}
          onChange={(e) => setFilters((f) => ({ ...f, fromDate: e.target.value }))}
          className="border p-2 rounded"
          max={filters.toDate || undefined}
          style={{marginRight:'10px'}}
        />
        <label htmlFor="checkout">Check-Out</label>
        <input
          type="date"
          value={filters.toDate}
          onChange={(e) => setFilters((f) => ({ ...f, toDate: e.target.value }))}
          className="border p-2 rounded"
          min={filters.fromDate || undefined}
          style={{marginRight:'10px'}}
        />
        <select
          value={filters.hotelId}
          onChange={(e) =>
            setFilters((f) => ({ ...f, hotelId: Number(e.target.value) }))
          }
          className="border p-2 rounded"
          style={{marginRight:'10px'}}
        >
          <option value="0">-- Select Hotel --</option>
          {hotelOptions.map((hotel) => (
            <option key={hotel.value} value={hotel.value}>
              {hotel.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleShow}
          className="btn btn-primary text-white rounded px-12 py-2 m-2"
        >
          Show
        </button>
      </div>
      {dateError && (
        <div className="alert alert-danger py-2 mb-2">{dateError}</div>
      )}

      <DataTableHeader columns={dailyRoomOccupancyColumns} data={data} />
      <div className="table-responsive">
        <DataTable
          columns={dailyRoomOccupancyColumns}
          data={currentItems}
          enablePagination={false}
        />
      </div>
      <DataTableFooter
        dataT={data}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
      />
    </div>
  );
};

export default DailyRoomOccupancyTable;
